import * as i0 from '@angular/core';
import { NgModule, Injectable } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as i1 from '@angular/router';
import { RouterModule, NavigationEnd } from '@angular/router';
import { Subject, of, throwError } from 'rxjs';
import { filter, tap, switchMap, catchError } from 'rxjs/operators';
import { v4 } from 'uuid';
import * as i1$1 from '@angular/common/http';
import { HttpHeaders } from '@angular/common/http';

class DatCoreModule {
}
DatCoreModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: DatCoreModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DatCoreModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: DatCoreModule, imports: [CommonModule,
        RouterModule] });
DatCoreModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: DatCoreModule, imports: [[
            CommonModule,
            RouterModule
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: DatCoreModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        RouterModule
                    ],
                    exports: [],
                    declarations: []
                }]
        }] });

var AppEventTypes;
(function (AppEventTypes) {
    AppEventTypes["Global"] = "global";
    AppEventTypes["UxInteractionAnalytics"] = "uxInteractionAnalytics";
    AppEventTypes["PerformanceAnalytics"] = "performanceAnalytics";
})(AppEventTypes || (AppEventTypes = {}));

var GlobalAppEvents;
(function (GlobalAppEvents) {
    GlobalAppEvents["RouteChangeComplete"] = "route change complete";
})(GlobalAppEvents || (GlobalAppEvents = {}));

var CoreEvents;
(function (CoreEvents) {
    CoreEvents["HttpRequestStarted"] = "HttpRequestStarted";
    CoreEvents["HttpRequestCompleted"] = "HttpRequestCompleted";
    CoreEvents["HttpRequestFailed"] = "HttpRequestFailed";
    CoreEvents["ShowLoadingOverlay"] = "ShowLoadingOverlay";
    CoreEvents["HideLoadingOverlay"] = "HideLoadingOverlay";
})(CoreEvents || (CoreEvents = {}));

class AppEventsService {
    constructor(router) {
        this.router = router;
        this.eventNames = [];
        this._eventStream = new Subject();
        this.initializeSubscriptions();
    }
    registerEvent(...events) {
        console.warn('[AppEventsService]: registering events is no longer required and is deprecated. This method will be removed in a future release.');
    }
    deregisterEvent(...events) {
        console.warn('[AppEventsService]: deregistering events is no longer necessary and is deprecated. This method will be removed in a future release.');
    }
    publishEvent(event) {
        this._eventStream.next(event);
    }
    get eventStream() {
        return this._eventStream.asObservable();
    }
    initializeSubscriptions() {
        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((navEndEvent) => {
            this.publishEvent({
                name: GlobalAppEvents.RouteChangeComplete,
                type: AppEventTypes.Global,
                data: navEndEvent
            });
        });
    }
}
AppEventsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AppEventsService, deps: [{ token: i1.Router }], target: i0.ɵɵFactoryTarget.Injectable });
AppEventsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AppEventsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AppEventsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.Router }]; } });

class DatHttpService {
    constructor(httpClient, eventService) {
        this.httpClient = httpClient;
        this.eventService = eventService;
        this.correlationId = '';
        this.correlationHeaderTitle = 'x-dat-correlation-id';
        this.startNewCorrelationSession();
    }
    /**
     * Generates a new correlation ID to be used.
     */
    startNewCorrelationSession() {
        this.correlationId = v4();
    }
    getCorrelationId() {
        return this.correlationId;
    }
    //noinspection JSUnusedGlobalSymbols
    get(request) {
        const requestUrl = this.generateFullyQualifiedUrl(request.serviceRootUri, request.queryParamString);
        return of(request).pipe(tap(this.handlePreRequestDuties.bind(this)), switchMap(() => this.httpClient.get(requestUrl, request.httpOptions)), tap(this.handlePostRequestDuties(request.appOptions)), catchError(this.handleError(request.appOptions)));
    }
    //noinspection JSUnusedGlobalSymbols
    post(request) {
        const requestUrl = this.generateFullyQualifiedUrl(request.serviceRootUri, request.queryParamString);
        return of(request).pipe(tap(this.handlePreRequestDuties.bind(this)), switchMap(() => this.httpClient.post(requestUrl, request.body, request.httpOptions)), tap(this.handlePostRequestDuties(request.appOptions)), catchError(this.handleError(request.appOptions)));
    }
    //noinspection ReservedWordAsName, JSUnusedGlobalSymbols
    delete(request) {
        const requestUrl = this.generateFullyQualifiedUrl(request.serviceRootUri, request.queryParamString);
        return of(request).pipe(tap(this.handlePreRequestDuties.bind(this)), switchMap(() => this.httpClient.delete(requestUrl, request.httpOptions)), tap(this.handlePostRequestDuties(request.appOptions)), catchError(this.handleError(request.appOptions)));
    }
    //noinspection JSUnusedGlobalSymbols
    put(request) {
        const requestUrl = this.generateFullyQualifiedUrl(request.serviceRootUri, request.queryParamString);
        return of(request).pipe(tap(this.handlePreRequestDuties.bind(this)), switchMap(() => this.httpClient.put(requestUrl, request.body, request.httpOptions)), tap(this.handlePostRequestDuties(request.appOptions)), catchError(this.handleError(request.appOptions)));
    }
    //noinspection JSUnusedGlobalSymbols
    patch(request) {
        const requestUrl = this.generateFullyQualifiedUrl(request.serviceRootUri, request.queryParamString);
        return of(request).pipe(tap(this.handlePreRequestDuties.bind(this)), switchMap(() => this.httpClient.patch(requestUrl, request.body, request.httpOptions)), tap(this.handlePostRequestDuties(request.appOptions)), catchError(this.handleError(request.appOptions)));
    }
    generateFullyQualifiedUrl(url = '', queryParams) {
        return (queryParams) ? `${url}?${queryParams}` : url;
    }
    handlePreRequestDuties(request) {
        if (request.appOptions.startNewCorrelationId) {
            this.startNewCorrelationSession();
        }
        if (request.appOptions.includeCorrelationId) {
            request.addHttpHeader(this.correlationHeaderTitle, this.getCorrelationId());
        }
        this.eventService.publishEvent({
            name: CoreEvents.HttpRequestStarted,
            data: {
                appOptions: request.appOptions
            }
        });
    }
    handlePostRequestDuties(options) {
        const _this = this; // prevents the need for a .bind(this) call
        return () => {
            _this.eventService.publishEvent({
                name: CoreEvents.HttpRequestCompleted,
                data: {
                    appOptions: options
                }
            });
        };
    }
    handleError(options) {
        const _this = this; // prevents the need for a .bind(this) call
        return (error) => {
            let errorMessage;
            if (error.status !== undefined && options.errorMessageMap.has(error.status)) {
                errorMessage = options.errorMessageMap.get(error.status);
            }
            else if (error.error != null && Array.isArray(error)) {
                errorMessage = `Error: ${error.error.map((e) => e.message).join(',')}`;
            }
            else {
                errorMessage = 'System error occurred. Please contact support if this persists.';
            }
            _this.eventService.publishEvent({
                name: CoreEvents.HttpRequestFailed,
                data: {
                    appOptions: options,
                    error: error,
                    errorMessage: errorMessage
                }
            });
            return throwError(error.error || error);
        };
    }
}
DatHttpService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: DatHttpService, deps: [{ token: i1$1.HttpClient }, { token: AppEventsService }], target: i0.ɵɵFactoryTarget.Injectable });
DatHttpService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: DatHttpService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: DatHttpService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1$1.HttpClient }, { type: AppEventsService }]; } });

/**
 * Immutable representation of an HTTP request that includes options for both the request behavior and application behavior
 */
class DatHttpRequest {
    constructor(serviceRootUri, queryParams, body, httpOptions, appOptions = {}) {
        this._serviceRootUri = serviceRootUri;
        this._queryParams = queryParams;
        this._body = body;
        this._httpOptions = httpOptions || DatHttpRequest.defaultHttpOptions;
        this._appOptions = Object.assign({
            ...DatHttpRequest.defaultAppOptions
        }, appOptions);
    }
    withServiceRootUri(endpointUrl) {
        this._serviceRootUri = endpointUrl;
        return this;
    }
    withQueryParams(queryParams) {
        this._queryParams = queryParams;
        return this;
    }
    withBody(body) {
        this._body = body;
        return this;
    }
    /**
     * See https://angular.io/api/common/http/HttpClient for what's allowed
     * @param httpOptions
     */
    withHttpOptions(httpOptions = DatHttpRequest.defaultHttpOptions) {
        this._httpOptions = httpOptions;
        return this;
    }
    withAppOptions(appOptions = DatHttpRequest.defaultAppOptions) {
        this._appOptions = Object.assign({
            ...DatHttpRequest.defaultAppOptions
        }, appOptions);
        return this;
    }
    addHttpHeader(key, value) {
        // ugly typecasting since there's no defined interface from Angular to make use of, and so httpOptions is of type 'object'
        const httpHeaders = this.httpOptions.headers;
        if (httpHeaders) {
            this._httpOptions.headers = httpHeaders.set(key, value);
        }
        else {
            this._httpOptions.headers = new HttpHeaders().set(key, value);
        }
        return this;
    }
    get serviceRootUri() {
        return this._serviceRootUri;
    }
    get body() {
        return this._body;
    }
    get httpOptions() {
        return this._httpOptions;
    }
    get appOptions() {
        return this._appOptions;
    }
    get queryParams() {
        return this._queryParams;
    }
    get queryParamString() {
        return this.objectToQueryString(this.queryParams);
    }
    /**
     * Converts an object to query/form string format
     * eg: {a:'1',b:'2'} -> a=1&b=2
     */
    objectToQueryString(data = {}) {
        let queryString = '';
        const buildQueryElement = (key, value) => {
            return value ? `${key}=${encodeURI(value)}&` : '';
        };
        Object.keys(data)
            .forEach((element) => {
            if (data[element] instanceof Array) {
                (data[element]).forEach((arrayElement) => {
                    queryString += buildQueryElement(element, arrayElement);
                });
            }
            else if (data[element] instanceof Date) {
                queryString += buildQueryElement(element, data[element].toISOString());
            }
            else {
                queryString += buildQueryElement(element, data[element]);
            }
        });
        return queryString.slice(0, -1); // remove trailing '&' (easier to do here as opposed to testing all values on creation);
    }
}
DatHttpRequest.defaultAppOptions = {
    loadingOverlayEnabled: true,
    toastOnSuccess: false,
    toastMessage: '',
    toastOnError: true,
    errorMessageMap: new Map(),
    startNewCorrelationId: false,
    includeCorrelationId: false
};
DatHttpRequest.defaultHttpOptions = {
    observe: 'body'
};
DatHttpRequest.concealedRequest = {
    loadingOverlayEnabled: false,
    toastOnSuccess: false,
    toastMessage: '',
    toastOnError: false,
    errorMessageMap: new Map(),
    startNewCorrelationId: false,
    includeCorrelationId: false
};

/*
 * Public API Surface of core
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AppEventTypes, AppEventsService, CoreEvents, DatCoreModule, DatHttpRequest, DatHttpService, GlobalAppEvents };
